import { DATE_PIPE_DEFAULT_OPTIONS, DatePipeConfig } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  isDevMode,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import {
  AuthInterceptor,
  AuthResourceTablePresetAdapter,
  authEffects,
  authFeatureKey,
  authReducer,
  authResourceTablePresetAdapterEffects,
} from '@kinet-open-network/client/auth/data-access';
import {
  API_URL,
  ENV_STAGE,
  SSD_API_URL,
  ZABBIX_API_URL,
  apiUrlInterceptor,
} from '@kinet-open-network/client/core/api-client';
import {
  errorHandlerEffects,
  errorHandlerFeatureKey,
  errorHandlerReducer,
  errorHandlingInterceptor,
} from '@kinet-open-network/client/core/error-handler';
import { provideDynamicForms } from '@kinet-open-network/client/core/forms/ui';
import {
  routerFeatureKey,
  routerReducer,
} from '@kinet-open-network/client/core/router';
import {
  RESOURCE_TABLE_STATE_KEY_PREFIX,
  provideResourceTablePresetAdapter,
} from '@kinet-open-network/client/shared/ui';
import { APP_VERSION } from '@kinet-open-network/client/shell/data-access';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { MessageService } from 'primeng/api';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      withEnabledBlockingInitialNavigation(),
      withComponentInputBinding(),
    ),
    provideStore({
      [authFeatureKey]: authReducer,
      [errorHandlerFeatureKey]: errorHandlerReducer,
      [routerFeatureKey]: routerReducer,
    }),
    provideEffects(
      authEffects,
      authResourceTablePresetAdapterEffects,
      errorHandlerEffects,
    ),
    // provideRouterStore(),
    isDevMode() ? provideStoreDevtools() : [],
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([apiUrlInterceptor, errorHandlingInterceptor]),
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: API_URL,
      useFactory: () => {
        if (
          environment.stage === 'production' ||
          environment.stage === 'staging'
        ) {
          const hostname = window.location.hostname;
          if (hostname.includes('telcohub.sk')) {
            return `https://${hostname}/api/v1`;
          } else {
            return `https://${hostname}:18444/api/v1`;
          }
        }
        return environment.api_url || 'http://localhost:3000/api/v2';
      },
    },
    {
      provide: SSD_API_URL,
      useValue:
        environment.ssd_api_url || 'http://localhost:3007/api/v1/open-network',
    },
    {
      provide: ZABBIX_API_URL,
      useValue: environment.zabbix_api_url || '',
    },
    {
      provide: LOCALE_ID,
      useValue: 'sk-SK',
    },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: {
        dateFormat: 'd. MMM yyyy, HH:mm',
      } satisfies DatePipeConfig,
    },
    {
      provide: RESOURCE_TABLE_STATE_KEY_PREFIX,
      useValue: 'kon_',
    },
    provideResourceTablePresetAdapter(AuthResourceTablePresetAdapter, {
      cacheMillis: 5 * 60 * 1000, // 5 minutes
    }),
    {
      provide: ENV_STAGE,
      useValue: environment.stage,
    },
    {
      provide: APP_VERSION,
      useValue: environment.version,
    },
    provideDynamicForms(),
    provideAnimations(),
    MessageService,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    {
      provide: ErrorHandler,
      useFactory: () => new BugsnagErrorHandler(),
    },
  ],
};
